import React, { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import Table from "themeComponents/Tables/Table";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import EmptyStateCard from "layouts/courses/components/EmptyStateCard";
import PageHeader from "layouts/courses/components/PageHeader";
import useSupportTickets from "hooks/useSupportTickets";
import useAuth from "hooks/useAuth";
import CreateTicketDialog from "./components/CreateTicketDialog";
import TicketDetailDialog from "./components/TicketDetailDialog";
import { SupportTicket, formatTicketStatus } from "types/support";

function SupportTickets(): React.JSX.Element {
  const { isAdmin } = useAuth();
  const canCreateTicket = !isAdmin;
  const {
    loading,
    tickets,
    listTickets,
    createTicket,
    getTicket,
    listComments,
    addComment,
    updateStatus,
    escalateTicket,
    uploadFile,
  } = useSupportTickets();

  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const load = useCallback(async () => {
    await listTickets();
  }, [listTickets]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = (ticket: SupportTicket) => {
    setSelectedTicketId(ticket.id);
    setDetailOpen(true);
  };

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  const columns = [
    { name: "subject", align: "left" as const, width: "35%" },
    { name: "status", align: "center" as const, width: "12%" },
    { name: "createdBy", align: "left" as const, width: "22%" },
    ...(isAdmin ? [{ name: "company", align: "left" as const, width: "15%" }] : []),
    { name: "date", align: "center" as const, width: "16%" },
  ];

  const rows = tickets.map((ticket) => ({
    subject: (
      <Typography
        variant="caption"
        fontWeight="medium"
        sx={{ cursor: "pointer", color: "info.main" }}
        onClick={() => openDetail(ticket)}
      >
        {ticket.subject}
      </Typography>
    ),
    status: (
      <Typography variant="caption" fontWeight="medium">
        {formatTicketStatus(ticket.status)}
      </Typography>
    ),
    createdBy: (
      <Typography variant="caption">
        {ticket.createdByDisplayName || ticket.createdByUserId}
      </Typography>
    ),
    ...(isAdmin
      ? {
          company: (
            <Typography variant="caption" color="text">
              {ticket.companyId || "—"}
            </Typography>
          ),
        }
      : {}),
    date: (
      <Typography variant="caption" color="text">
        {formatDate(ticket.createdAt)}
      </Typography>
    ),
  }));

  return (
    <DashboardLayout data-testid="support-tickets-page">
      <DashboardNavbar />
      <Box py={3}>
        <PageHeader
          title="Support tickets"
          subtitle={
            isAdmin
              ? "View and manage support tickets from users."
              : "Report issues and communicate with admins."
          }
          actions={
            canCreateTicket ? (
              <Button
                color="info"
                variant="gradient"
                data-testid="support-open-create-dialog"
                onClick={() => setCreateOpen(true)}
              >
                Create ticket
              </Button>
            ) : undefined
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loader loading={true} {...({} as any)} />
          </Box>
        ) : tickets.length === 0 ? (
          <EmptyStateCard
            title="No tickets yet"
            description={
              isAdmin
                ? "Support tickets from users will appear here."
                : "Create a ticket to get help from your company or platform admins."
            }
            actionLabel={canCreateTicket ? "Create ticket" : undefined}
            onAction={canCreateTicket ? () => setCreateOpen(true) : undefined}
          />
        ) : (
          <Card>
            <Table columns={columns} rows={rows} />
          </Card>
        )}
      </Box>
      <Footer />

      {canCreateTicket ? (
        <CreateTicketDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onUpload={uploadFile}
          onSubmit={async (values) => {
            await createTicket(values);
            await load();
          }}
        />
      ) : null}

      <TicketDetailDialog
        open={detailOpen}
        ticketId={selectedTicketId}
        onClose={() => {
          setDetailOpen(false);
          setSelectedTicketId(null);
        }}
        onRefreshList={load}
        getTicket={getTicket}
        listComments={listComments}
        addComment={addComment}
        updateStatus={updateStatus}
        escalateTicket={escalateTicket}
        onUpload={uploadFile}
      />
    </DashboardLayout>
  );
}

export default SupportTickets;
